import React, { useState, useEffect, lazy, Suspense } from 'react';
import FeatureToggle from './components/FeatureToggle';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Heart, User, MessageCircle, Menu, X, Home as HomeIcon, Plus } from 'lucide-react';
import { useFeatureFlag, FLAGS } from './lib/featureFlags';
import WigEmoticon from './components/WigEmoticon';
import ScrollToTop from './components/ScrollToTop';
import TestModeIndicator from './components/TestModeIndicator';
const Accessibility = lazy(() => import('./pages/legal/Accessibility'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const PaymentMethods = lazy(() => import('./pages/help/PaymentMethods'));
import Home from './pages/Home';
import BrandPage from './pages/BrandPage';
import SearchPage from './pages/Search';
import Register from './pages/Register';
import Login from './pages/Login';
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
import AuthCallback from './pages/AuthCallback';
const AuthConfirmation = lazy(() => import('./pages/AuthConfirmation'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const Messages = lazy(() => import('./pages/Messages'));
const Legal = lazy(() => import('./pages/Legal'));
const Resources = lazy(() => import('./pages/Resources'));
const Press = lazy(() => import('./pages/Press'));
const About = lazy(() => import('./pages/About'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const RewardsTerms = lazy(() => import('./pages/RewardsTerms'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
// Fix import path for Subscription component
const Subscription = lazy(() => import('./pages/Subscription'));
const PaymentReturn = lazy(() => import('./pages/PaymentReturn'));
const Pricing = lazy(() => import('./pages/Pricing'));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const CreatingAccount = lazy(() => import('./pages/help/CreatingAccount'));
const ListingFirstItem = lazy(() => import('./pages/help/ListingFirstItem'));
const SafetyGuidelines = lazy(() => import('./pages/help/SafetyGuidelines'));
const ShippingGuidelines = lazy(() => import('./pages/help/ShippingGuidelines'));
const CommunicationGuidelines = lazy(() => import('./pages/help/CommunicationGuidelines'));
const ReturnPolicy = lazy(() => import('./pages/help/ReturnPolicy'));
const RewardsHelp = lazy(() => import('./pages/help/Rewards'));
const Contact = lazy(() => import('./pages/Contact'));
import ListingDetails from './pages/ListingDetails';
const Rewards = lazy(() => import('./pages/Rewards'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import { useAuth } from './contexts/AuthContext';
import { signOut } from './lib/auth';
import { checkForPaymentReturn, attemptSessionRecovery } from './lib/sessionRecovery';

const categories = [
  { id: 'belle-tress', name: 'Belle Tress', type: 'brand' },
  { id: 'amore', name: 'Amore', type: 'brand' },
  { id: 'ellen-wille', name: 'Ellen Wille', type: 'brand' },
  { id: 'envy', name: 'Envy', type: 'brand' },
  { id: 'estetica', name: 'Estetica', type: 'brand' },
  { id: 'gabor', name: 'Gabor', type: 'brand' },
  { id: 'henry-margu', name: 'Henry Margu', type: 'brand' },
  { id: 'jon-renau', name: 'Jon Renau', type: 'brand' },
  { id: 'mane-attraction', name: 'Mane Attraction', type: 'brand' },
  { id: 'noriko', name: 'Noriko', type: 'brand' },
  { id: 'raquel-welch', name: 'Raquel Welch', type: 'brand' },
  { id: 'rene-of-paris', name: 'Rene of Paris', type: 'brand' },
  { id: 'tony-of-beverly', name: 'Tony of Beverly', type: 'brand' },
  { id: 'tressallure', name: 'TressAllure', type: 'brand' },
  { id: 'wigpro', name: 'WigPro', type: 'brand' }
];

function SearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <form onSubmit={handleSearch} className="relative w-full">
      <input
        type="text"
        placeholder="Search for wigs..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
      />
      <button
        type="submit"
        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
      >
        <Search size={20} />
      </button>
    </form>
  );
}

function useUnreadCount(enabled: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const { getUnreadCount } = await import('./lib/messages');
        const { count } = await getUnreadCount();
        if (!cancelled) setCount(count);
      } catch {
        // non-fatal; badge just stays stale
      }
    };
    refresh();
    const interval = setInterval(refresh, 60000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);

  return count;
}

function UnreadDot({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[11px] font-semibold flex items-center justify-center">
      {count > 9 ? '9+' : count}
    </span>
  );
}

function MobileTabBar({ isAuthed, unreadCount }: { isAuthed: boolean; unreadCount: number }) {
  const location = useLocation();

  const tabClass = (active: boolean) =>
    `p-2 flex flex-col items-center gap-0.5 text-[11px] font-medium transition-colors ${
      active ? 'text-rose-600' : 'text-gray-500'
    }`;

  const isHome = location.pathname === '/';
  const isSearch = location.pathname.startsWith('/search') || location.pathname.startsWith('/brand');
  const isSaved = location.pathname.startsWith('/account/favorites');
  const isMessages = location.pathname.startsWith('/messages');

  return (
    <div
      className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 flex justify-around items-end z-40 py-1 pb-[max(0.25rem,env(safe-area-inset-bottom))]"
      role="navigation"
      aria-label="Primary"
    >
      <Link to="/" className={tabClass(isHome)} aria-current={isHome ? 'page' : undefined}>
        <HomeIcon className="h-5 w-5" />
        <span>Home</span>
      </Link>
      <Link to="/search" className={tabClass(isSearch)} aria-current={isSearch ? 'page' : undefined}>
        <Search className="h-5 w-5" />
        <span>Search</span>
      </Link>
      <Link
        to={isAuthed ? '/account/create-listing' : '/register'}
        className="-mt-5 flex flex-col items-center gap-0.5 text-[11px] font-medium text-rose-600"
        aria-label="Sell a wig"
      >
        <span className="h-12 w-12 rounded-full bg-rose-600 text-white shadow-lg flex items-center justify-center">
          <Plus className="h-6 w-6" />
        </span>
        <span>Sell</span>
      </Link>
      <Link to="/account/favorites" className={tabClass(isSaved)} aria-current={isSaved ? 'page' : undefined}>
        <Heart className="h-5 w-5" />
        <span>Saved</span>
      </Link>
      {isAuthed ? (
        <Link to="/messages" className={tabClass(isMessages)} aria-current={isMessages ? 'page' : undefined}>
          <span className="relative">
            <MessageCircle className="h-5 w-5" />
            <UnreadDot count={unreadCount} />
          </span>
          <span>Messages</span>
        </Link>
      ) : (
        <Link to="/login" className={tabClass(false)}>
          <User className="h-5 w-5" />
          <span>Login</span>
        </Link>
      )}
    </div>
  );
}

function BrandBar() {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, []);

  const nudge = (dir: 1 | -1) => {
    scrollerRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  return (
    <nav className="hidden md:block py-2" aria-label="Shop by brand">
      <div className="relative flex items-center">
        {canLeft && (
          <button
            onClick={() => nudge(-1)}
            aria-label="Scroll brands left"
            className="absolute left-0 z-10 h-8 w-8 flex items-center justify-center rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-rose-600"
          >
            ‹
          </button>
        )}
        <div
          ref={scrollerRef}
          className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2 px-1"
        >
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/brand/${category.id}`}
              className="text-gray-600 hover:text-rose-700 hover:bg-rose-50 rounded-full transition-colors text-sm font-medium whitespace-nowrap px-3 py-1.5 flex-shrink-0"
            >
              {category.name}
            </Link>
          ))}
          <Link
            to="/search"
            className="text-rose-600 hover:text-rose-700 text-sm font-semibold whitespace-nowrap px-3 py-1.5 flex-shrink-0"
          >
            All brands →
          </Link>
        </div>
        {canRight && (
          <>
            <div className="pointer-events-none absolute right-9 top-0 h-full w-16 bg-gradient-to-l from-white to-transparent" />
            <button
              onClick={() => nudge(1)}
              aria-label="Scroll brands right"
              className="absolute right-0 z-10 h-8 w-8 flex items-center justify-center rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-rose-600"
            >
              ›
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

function App() {
  const { user, checkSubscriptionStatus } = useAuth();
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const unreadCount = useUnreadCount(!!user);
  
  // Check for users returning from payment who might have lost their session
  useEffect(() => {
    const handlePaymentReturn = async () => {
      // Only try recovery once per page load
      if (recoveryAttempted) return;
      
      // Check if URL indicates a return from payment
      if (checkForPaymentReturn()) {
        console.log('Detected return from payment, attempting session recovery');
        const recovered = await attemptSessionRecovery();
        setRecoveryAttempted(true);
        
        if (recovered) {
          // Force check subscription status after recovery
          setTimeout(() => {
            checkSubscriptionStatus(true);
          }, 2000);
        }
      }
    };
    
    handlePaymentReturn();
  }, [recoveryAttempted, checkSubscriptionStatus]);
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBrandMenuOpen, setIsBrandMenuOpen] = useState(false);
  
  // Check for mobile UI improvements feature flag
  const useMobileUi = useFeatureFlag(FLAGS.MOBILE_UI_IMPROVEMENTS);
  const useImprovedHamburger = useFeatureFlag(FLAGS.IMPROVED_HAMBURGER);
  
  // Close the menu when clicking outside of it
  useEffect(() => {
    if (isMenuOpen && useImprovedHamburger) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.mobile-menu-container') && !target.closest('.menu-toggle-button')) {
          setIsMenuOpen(false);
        }
      };
      
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMenuOpen, useImprovedHamburger]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isBrandMenuOpen) setIsBrandMenuOpen(false);
  };

  const toggleBrandMenu = () => {
    setIsBrandMenuOpen(!isBrandMenuOpen);
    if (isMenuOpen) setIsMenuOpen(false);
  };
  
  // Function to close menus when a link is clicked
  const closeMenus = () => {
    setIsMenuOpen(false);
    setIsBrandMenuOpen(false);
  };

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <TestModeIndicator />
      <div className={`min-h-screen bg-gray-50 flex flex-col pb-16 md:pb-0 ${useMobileUi ? 'mobile-optimized' : ''}`}>
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Top Bar */}
            <div className={`py-4 flex items-center justify-between ${useMobileUi ? 'mobile-header-spacing' : ''}`}>
              {/* Logo */}
              <Link to="/" className="flex-shrink-0">
                <div className="flex items-center gap-3">
                  <WigEmoticon className="flex-shrink-0" />
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-primary-600 tracking-tight">WigConnection</div>
                    <div className="hidden lg:block text-xs text-gray-500">Buy, Sell, Trade — Premium Wigs</div>
                  </div>
                </div>
              </Link>

              {/* Desktop Search */}
              <div className="hidden md:flex flex-1 max-w-2xl mx-8">
                <SearchBar />
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-1 lg:gap-2">
                <Link
                  to="/how-it-works"
                  className="px-3 py-2 rounded-full text-gray-700 hover:text-rose-700 hover:bg-rose-50 transition-colors font-medium whitespace-nowrap"
                >
                  How It Works
                </Link>
                <Link
                  to="/pricing"
                  className="px-3 py-2 rounded-full text-gray-700 hover:text-rose-700 hover:bg-rose-50 transition-colors font-medium"
                >
                  Pricing
                </Link>
                <Link
                  to={user ? "/" : "/login"}
                  onClick={async (e) => {
                    if (user) {
                      e.preventDefault();
                      try {
                        await signOut();
                      } catch (error) {
                        console.error('Sign out error:', error);
                      }
                    }
                  }}
                  className="px-3 py-2 rounded-full text-gray-700 hover:text-rose-700 hover:bg-rose-50 transition-colors font-medium whitespace-nowrap"
                >
                  {user ? 'Sign Out' : 'Sign In'}
                </Link>

                <Link
                  to={user ? '/account/create-listing' : '/register'}
                  className="ml-1 mr-2 px-5 py-2 rounded-full bg-rose-600 text-white font-medium whitespace-nowrap shadow-sm hover:bg-rose-700 hover:shadow transition-all"
                >
                  Sell a Wig
                </Link>

                {user && (
                  <Link 
                    to="/messages" 
                    className="relative p-2 rounded-full text-gray-600 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                    aria-label={unreadCount > 0 ? `Messages, ${unreadCount} unread` : 'Messages'}
                  >
                    <MessageCircle className="h-6 w-6" />
                    <UnreadDot count={unreadCount} />
                  </Link>
                )}
                <Link 
                  to={user ? "/account" : "/login"}
                  className="p-2 rounded-full text-gray-600 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                  aria-label="My account"
                >
                  <User className="h-6 w-6" />
                </Link>
                <Link
                  to="/account/favorites"
                  className="p-2 rounded-full text-gray-600 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                  aria-label="Saved wigs"
                >
                  <Heart className="h-6 w-6" />
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <div className="flex md:hidden items-center space-x-4">
                <button
                  onClick={toggleMenu}
                  className="text-gray-600 hover:text-rose-600 transition-colors ml-4 menu-toggle-button"
                >
                  {isMenuOpen && !useImprovedHamburger ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>

            {/* Mobile Search - Always visible */}
            <div className={`md:hidden py-2 ${useMobileUi ? 'mobile-search-improved' : ''}`}>
              <SearchBar />
            </div>

            {/* Mobile Menu - Either improved or standard version based on feature flag */}
            {useImprovedHamburger && isMenuOpen ? (
              <div 
                className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 md:hidden transition-opacity duration-300 opacity-100"
                onClick={toggleMenu}
              >
                <div 
                  className="bg-white w-4/5 max-w-sm h-full shadow-xl transform transition-transform duration-300 mobile-menu-container translate-x-0 flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                  style={{ transform: isMenuOpen ? 'translateX(0)' : 'translateX(-100%)' }}
                >
                  <div className="p-4 border-b flex justify-between items-center">
                    <h2 className="font-medium text-lg">Menu</h2>
                    <button onClick={toggleMenu} className="p-2 rounded-md text-gray-500 hover:text-gray-700">
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="py-2 space-y-1">
                    <Link
                      to="/how-it-works"
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                      onClick={closeMenus}
                    >
                      How It Works
                    </Link>
                    <Link
                      to="/pricing"
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                      onClick={closeMenus}
                    >
                      Pricing
                    </Link>
                    <Link
                      to={user ? "/" : "/login"}
                      onClick={async (e) => {
                        if (user) {
                          e.preventDefault();
                          try {
                            await signOut();
                            closeMenus();
                          } catch (error) {
                            console.error('Sign out error:', error);
                          }
                        } else {
                          closeMenus();
                        }
                      }}
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {user ? 'Sign Out' : 'Sign In'}
                    </Link>
                    {user && (
                      <Link
                        to="/messages"
                        className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                        onClick={closeMenus}
                      >
                        Messages
                      </Link>
                    )}
                    <Link
                      to={user ? "/account" : "/login"}
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                      onClick={closeMenus}
                    >
                      {user ? 'My Account' : 'My Account'}
                    </Link>
                    <Link
                      to="/account/favorites"
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                      onClick={closeMenus}
                    >
                      Saved Listings
                    </Link>
                  </div>
                  {/* Categories incorporated into mobile menu */}
                  <div className="px-2 pt-2 pb-3 border-t mt-2 flex-1 overflow-hidden flex flex-col">
                    <h3 className="px-3 py-2 text-sm font-medium text-gray-500">Shop by Brand</h3>
                    <div className="space-y-1 overflow-y-auto flex-1 pb-4">
                      {categories.map((category) => (
                        <Link
                          key={category.id}
                          to={`/brand/${category.id}`}
                          className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                          onClick={closeMenus}
                        >
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  </div>
              </div>
              </div>
            ) : (isMenuOpen && (
              <div className="md:hidden">
                <div className="py-2 space-y-1">
                  <Link
                    to="/how-it-works"
                    className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    How It Works
                  </Link>
                  <Link
                    to="/pricing"
                    className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link
                    to={user ? "/" : "/login"}
                    onClick={async (e) => {
                      if (user) {
                        e.preventDefault();
                        try {
                          await signOut();
                        } catch (error) {
                          console.error('Sign out error:', error);
                        }
                      }
                    }}
                    className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {user ? 'Sign Out' : 'Sign In'}
                  </Link>
                  {user && (
                    <Link
                      to="/messages"
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Messages
                    </Link>
                  )}
                  <Link
                    to={user ? "/account" : "/login"}
                    className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {user ? 'My Account' : 'My Account'}
                  </Link>
                  <Link
                    to="/account/favorites"
                    className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Saved Listings
                  </Link>
                </div>
              </div>
            ))}

            {/* Desktop Categories */}
            <BrandBar />

            {/* Mobile Categories Button - Only shown if not using improved hamburger */}
            {!useImprovedHamburger && (
              <div className="md:hidden py-2 border-t">
                <button
                  onClick={toggleBrandMenu}
                  className="flex items-center justify-between w-full px-4 py-2 text-base font-medium text-gray-700"
                >
                  <span>Browse Brands</span>
                  <span className={`transform transition-transform ${isBrandMenuOpen ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
              </div>
            )}

            {/* Mobile Categories Menu - Standard version (only shown if not using improved hamburger) */}
            {!useImprovedHamburger && (
              <div className={`md:hidden ${isBrandMenuOpen ? 'block' : 'hidden'}`}>
                <div className="py-2 space-y-1 max-h-64 overflow-y-auto">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/brand/${category.id}`}
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsBrandMenuOpen(false)}
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Feature toggle for admins */}
        <FeatureToggle />

        {/* Main Content */}
        <div className={`flex-1 ${useMobileUi ? 'mobile-content-spacing' : ''}`}>
          <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/brand/:brandId" element={<BrandPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/confirmation" element={<AuthConfirmation />} />
            <Route path="/legal/:page" element={<Legal />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/press" element={<Press />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/rewards-terms" element={<RewardsTerms />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/help/creating-account" element={<CreatingAccount />} />
            <Route path="/help/listing-first-item" element={<ListingFirstItem />} />
            <Route path="/help/safety-guidelines" element={<SafetyGuidelines />} />
            <Route path="/help/shipping" element={<ShippingGuidelines />} />
            <Route path="/help/payment-methods" element={<PaymentMethods />} />
            <Route path="/help/communication" element={<CommunicationGuidelines />} />
            <Route path="/help/returns" element={<ReturnPolicy />} />
            <Route path="/help/rewards" element={<RewardsHelp />} />
            <Route path="/legal/accessibility" element={<Accessibility />} />
            <Route path="/legal/cookies" element={<CookiePolicy />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/listing/:id" element={<ListingDetails />} />
            <Route path="/user/:userId" element={<UserProfile />} />
            <Route
              path="/account/*"
              element={
                <ProtectedRoute requireSubscription>
                  <MyAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute requireSubscription>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rewards"
              element={
                <ProtectedRoute>
                  <Rewards />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              }
            />
            {/* Payment Return - Deliberately NOT protected since users need to restore session */}
            <Route
              path="/payment-return"
              element={<PaymentReturn />}
            />
            {/* Original admin route */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Alternative route for new admin dashboard */}
            <Route
              path="/admin-dashboard/*"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
        </div>

        {/* Footer */}
        <Footer />
        
        {/* Feature Toggle for Admin Users */}
        {/* {user && user.app_metadata?.role === 'admin' && <FeatureToggle />} */}
        
        <MobileTabBar isAuthed={!!user} unreadCount={unreadCount} />
      </div>
    </Router>
  );
}

export default App;